import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

const STORAGE_KEY_PREFIX = 'popup-newsletter-hide-until:';

/**
 * A dialog that automatically opens itself after a delay (unless the visitor
 * already dismissed or subscribed recently) and remembers dismissals in
 * localStorage.
 *
 * @extends DialogComponent
 */
class PopupNewsletterDialog extends DialogComponent {
  /** @type {number | undefined} */
  #timeoutId;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    if (this.forceOpen) {
      if (this.markSubscribed) this.#persistHideUntil();
      this.showDialog();
      return;
    }

    if (this.#isHidden()) return;

    this.#timeoutId = window.setTimeout(() => {
      this.showDialog();
    }, this.delaySeconds * 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    if (this.#timeoutId) window.clearTimeout(this.#timeoutId);
  }

  #handleDialogClose = () => {
    this.#persistHideUntil();
  };

  #persistHideUntil() {
    try {
      const hideUntil = Date.now() + this.hideDays * 24 * 60 * 60 * 1000;
      window.localStorage.setItem(this.#storageKey, String(hideUntil));
    } catch (error) {
      // localStorage may be unavailable (private browsing, disabled storage, etc).
    }
  }

  #isHidden() {
    try {
      const hideUntil = Number(window.localStorage.getItem(this.#storageKey));
      return Boolean(hideUntil) && hideUntil > Date.now();
    } catch (error) {
      return false;
    }
  }

  get #storageKey() {
    return `${STORAGE_KEY_PREFIX}${this.id || 'default'}`;
  }

  get delaySeconds() {
    return Number(this.getAttribute('delay-seconds')) || 0;
  }

  get hideDays() {
    return Number(this.getAttribute('hide-days')) || 7;
  }

  get forceOpen() {
    return this.hasAttribute('force-open');
  }

  get markSubscribed() {
    return this.hasAttribute('mark-subscribed');
  }
}

if (!customElements.get('popup-newsletter-dialog')) {
  customElements.define('popup-newsletter-dialog', PopupNewsletterDialog);
}
